import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDebouncedCallback } from '../../hooks/useDebounced';

interface EnhancedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  showSearchTips?: boolean;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

const EnhancedSearchInput: React.FC<EnhancedSearchInputProps> = ({
  value,
  onChange,
  placeholder = "Search...",
  debounceMs = 300,
  className = "",
  showSearchTips = false,
  autoFocus = false,
  onFocus,
  onBlur
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusPreservationRef = useRef<boolean>(false);

  // Debounced callback to update parent component
  const debouncedOnChange = useDebouncedCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    debounceMs,
    [onChange]
  );

  // Update local value when prop value changes (only if different from current local value)
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]); // Removed localValue from deps to prevent infinite loop

  // Focus preservation mechanism - maintains focus during re-renders
  useEffect(() => {
    if (focusPreservationRef.current && inputRef.current && document.activeElement !== inputRef.current) {
      // Restore focus if it was lost during a re-render while user was typing
      console.log('🎯 EnhancedSearchInput: Restoring focus after re-render');
      inputRef.current.focus();
    }
  });

  // Monitor for external re-renders that might steal focus
  useEffect(() => {
    if (!inputRef.current) return;

    const handleFocusLoss = () => {
      if (focusPreservationRef.current) {
        // If we're actively preserving focus and it gets lost, restore it quickly
        setTimeout(() => {
          if (focusPreservationRef.current && inputRef.current) {
            console.log('🎯 EnhancedSearchInput: Focus lost, attempting to restore');
            inputRef.current.focus();
          }
        }, 10); // Very short delay to allow for legitimate focus changes
      }
    };

    const input = inputRef.current;
    input.addEventListener('blur', handleFocusLoss);
    
    return () => {
      input.removeEventListener('blur', handleFocusLoss);
    };
  }, []);

  // Auto focus if requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Activate focus preservation when user starts typing
    if (!focusPreservationRef.current) {
      focusPreservationRef.current = true;
      console.log('🎯 EnhancedSearchInput: Activating focus preservation mode');
    }
    
    // Only call debounced change if the value actually changed
    if (newValue !== value) {
      debouncedOnChange(newValue);
    }
  }, [value, debouncedOnChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    focusPreservationRef.current = true; // Always activate focus preservation on focus
    if (showSearchTips) {
      setShowTips(true);
    }
    console.log('🎯 EnhancedSearchInput: Focus gained, activating focus preservation');
    onFocus?.();
  }, [showSearchTips, onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Only deactivate focus preservation after a delay to handle legitimate blur events
    setTimeout(() => {
      // Only deactivate if the input is not focused after the delay
      if (inputRef.current && document.activeElement !== inputRef.current) {
        focusPreservationRef.current = false;
        console.log('🎯 EnhancedSearchInput: Deactivating focus preservation after legitimate blur');
      }
    }, 100);
    
    // Delay hiding tips to allow clicking on them
    setTimeout(() => setShowTips(false), 150);
    onBlur?.();
  }, [onBlur]);

  const clearSearch = useCallback(() => {
    setLocalValue('');
    onChange('');
    inputRef.current?.focus();
    // Keep focus preservation active after clearing
    focusPreservationRef.current = true;
  }, [onChange]);

  // Handle keyboard events to maintain focus preservation during typing
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Activate focus preservation on any meaningful key press
    if (!focusPreservationRef.current && (
      e.key.length === 1 || // Printable characters
      e.key === 'Backspace' || 
      e.key === 'Delete' || 
      e.key === 'Enter'
    )) {
      focusPreservationRef.current = true;
      console.log('🎯 EnhancedSearchInput: Focus preservation activated by keypress:', e.key);
    }
  }, []);

  const hasQuotedTerm = localValue.includes('"') || localValue.includes("'");

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${className}`}
        />
        
        {/* Search Icon */}
        <div className="absolute left-3 top-2.5 pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Clear Button */}
        {localValue && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            type="button"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Quote Indicator */}
        {hasQuotedTerm && (
          <div className="absolute right-9 top-2.5 pointer-events-none">
            <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </div>
        )}
      </div>

      {/* Search Tips */}
      {showSearchTips && showTips && (isFocused || localValue.includes('"') || localValue.includes("'")) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">Search Tips:</div>
            <div>• Type normally to search across all fields</div>
            <div>• Use <span className="px-1 bg-gray-100 dark:bg-gray-700 rounded">&quot;exact phrase&quot;</span> for exact matches</div>
            <div>• Multiple terms will all be matched</div>
            <div>• Search is case-insensitive</div>
            {hasQuotedTerm && (
              <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-700 dark:text-blue-300">
                <div className="flex items-center space-x-1">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs">Exact match mode active</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {isFocused && localValue !== value && (
        <div className="absolute right-3 top-2.5 pointer-events-none">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSearchInput;